import React, { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { cancelSubscription,
  postUpdateInformation,
  uploadFile } from '../../../api/api'
import Loading from '../../../Components/loading/Loading'
import {
  requestSettings,
  settingsUpdateAvatarUrl,
  settingsUpdateDefaultLocation,
  settingsUpdateFirstName,
  settingsUpdateLastName,
  settingsUpdatePhoneNumber,
  settingsUpdateVirtualInfo,
} from '../../../store/settings/actions'
import Location from '../../../Components/form/location-input/Location'
import { Avatar } from '../../../Components/avatar/Avatar'
import { Tiers } from '../../../types/plann3r'
import { Plann3rStore } from '../../../store/types'
import { SettingsStore } from '../../../store/settings/types'

const InformationTab: FC<{
  showSubscriptionPopup: () => void,
}> = ({ showSubscriptionPopup }) => {
  const dispatch = useDispatch()

  const strings = useSelector(
    (state: Plann3rStore) => state.languages.strings.settings.settings,
  )

  const {
    first_name,
    last_name,
    email,
    phone_number,
    virtual_info,
    timezone,
    default_location,
    avatar_url,
    tier,
  } = useSelector((state: Plann3rStore): SettingsStore => state.settings)

  const [saving, setsaving] = useState(false)
  const [uploadingLogo, setuploadingLogo] = useState(false)

  const updateInformation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setsaving(true)

    const response = await postUpdateInformation({
      first_name,
      last_name,
      email,
      phone_number,
      virtual_info,
      timezone,
      default_location,
      avatar_url,
    })

    setsaving(false)

    if (response.success) {
      dispatch(requestSettings())
    } else {
      alert(strings.update_information_error)
    }
  }

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    
    const file = e.target.files ? e.target.files[0] : null

    if (file && file.name.match(/.(jpg|jpeg|png|gif)$/i)) {
      if (file.size < 2000000) {
        setuploadingLogo(true)
        const response = await uploadFile(file, 'plann3r.profilepictures')

        if (response.success) {
          dispatch(settingsUpdateAvatarUrl(response.url))
          setuploadingLogo(false)
        } else {
          alert(strings.upload_file_error)
        }
      } else {
        alert(strings.file_size_error)
        setuploadingLogo(false)
      }
    } else {
      alert(strings.file_type_error)
      setuploadingLogo(false)
    }
  }

  return (
    <div className="columns styles setting information">
      <div className="column is-half">
        <form onSubmit={updateInformation}>
          <div>
            {!saving && (
              <button className="button is-primary">
                {strings.save_button}
              </button>
            )}
            {saving && (
              <div>
                <span className="has-text-centered">{strings.saving}</span>
                <Loading />
              </div>
            )}
          </div>
          <div>
            <label htmlFor="first_name">{strings.first_name_label}</label>
            <input
              id="first_name"
              className="grey full-width"
              type="text"
              data-lpignore="true"
              value={first_name}
              placeholder={strings.first_name_placeholder}
              onChange={(event) =>
                dispatch(settingsUpdateFirstName(event.target.value))
              }
              required
            />
          </div>
          <div>
            <label htmlFor="last_name">{strings.last_name_label}</label>
            <input
              id="last_name"
              className="grey full-width"
              type="text"
              data-lpignore="true"
              value={last_name}
              placeholder={strings.last_name_placeholder}
              onChange={(event) =>
                dispatch(settingsUpdateLastName(event.target.value))
              }
              required
            />
          </div>
          <div>
            <label htmlFor="email">{strings.email_label}</label>
            <input
              id="email"
              className="grey full-width cursor-not-allowed"
              type="email"
              value={email}
              placeholder={strings.email_placeholder}
              readOnly
              required
            />
          </div>
          <div>
            <label htmlFor="phone_number">{strings.phone_label}</label>
            <input
              id="phone_number"
              className="grey full-width"
              type="text"
              data-lpignore="true"
              value={phone_number}
              placeholder={strings.phone_placeholder}
              onChange={(event) =>
                dispatch(settingsUpdatePhoneNumber(event.target.value))
              }
            />
          </div>
          <div>
            <label htmlFor="virtual_info">{strings.videocall_label}</label>
            <input
              id="virtual_info"
              className="grey full-width"
              type="text"
              value={virtual_info}
              placeholder={strings.videocall_placeholder}
              data-lpignore="true"
              onChange={(event) =>
                dispatch(settingsUpdateVirtualInfo(event.target.value))
              }
            />
          </div>
          <div>
            <label htmlFor="default_location">{strings.location_label}</label>
            <Location
              onChange={(val: string) =>
                dispatch(settingsUpdateDefaultLocation(val))
              }
              default_location={default_location}
            />
          </div>
          <div>
            <label htmlFor="tier">{strings.tier}</label>
            <input
              id="tier"
              className="grey full-width cursor-not-allowed"
              type="text"
              value={tier + ''}
              placeholder={strings.tier}
              data-lpignore="true"
              readOnly
              required
            />
          </div>
          <div>
            {!saving && (
              <button className="button is-primary">
                {strings.save_button}
              </button>
            )}
            {saving && (
              <div>
                <span className="has-text-centered">{strings.saving}</span>
                <Loading />
              </div>
            )}
          </div>
        </form>
      </div>
      <div className="column">
        <div className="has-text-centered mb-8 flex flex-col items-center">
          <Avatar
            url={
              avatar_url ??
              'https://s3-us-west-2.amazonaws.com/plann3r.profilepictures/avatar.png'
            }
            label={first_name + ' ' + last_name}
          />
          <div>
            <b>{strings.change_profile_picture}</b>
            <br />
            {uploadingLogo && <span>{strings.dont_save}</span>}
            {uploadingLogo && <Loading />}
            {!uploadingLogo && <input type="file" onChange={uploadImage} />}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {tier?.toLowerCase() !== Tiers.PROFESSIONAL && (
            <button
              className="button is-primary"
              style={{ margin: '5px auto' }}
              onClick={ () => showSubscriptionPopup() }
            >
              {strings.upgrade_subscription}
            </button>
          )}
          {(tier?.toLowerCase() === Tiers.PLUS || tier?.toLowerCase() === Tiers.PROFESSIONAL) && (
            <button
              className="button my-4 m-auto"
              onClick={ async () => {
                if (confirm(strings.cancel_confirmation)) {
                  const response = await cancelSubscription()

                  if (response.success) {
                    dispatch(requestSettings())
                  }
                }
              }}
            >
              {strings.cancel_subscription}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default InformationTab
